// assets/product-custom-property.js
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 * @property {HTMLElement} conditionalWrapper - The block wrapper, carries the conditional display config.
 */

/**
 * A custom element that manages product custom properties.
 *
 * When the block is configured with `visible_for_option_values`, it shows itself only while the
 * selected variant matches one of those option values, and disables its input while hidden so the
 * line item property is never submitted with the product form.
 *
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  /** @type {Element | null} */
  #eventTarget = null;

  connectedCallback() {
    super.connectedCallback();

    if (!this.#isConditional) return;

    this.#eventTarget = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  handleInput() {
    this.#updateCharacterCount();
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }

  get #isConditional() {
    return this.refs.conditionalWrapper?.dataset.conditional === 'true';
  }

  /**
   * Handles the product select event and shows or hides the block for the newly selected variant.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        const resource = detail?.resource;
        if (!resource) return;

        const optionValues = resource.options ?? [resource.option1, resource.option2, resource.option3];
        this.#setVisibility(this.#matchesTriggers(optionValues));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-custom-property] Event promise rejected:', error);
      });
  };

  /**
   * Whether any of the selected option values matches a configured trigger value.
   * @param {(string | null | undefined)[]} optionValues - The selected variant's option values.
   * @returns {boolean}
   */
  #matchesTriggers(optionValues) {
    const triggers = (this.refs.conditionalWrapper?.dataset.visibleFor ?? '')
      .split(',')
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);

    if (!triggers.length) return true;

    const selected = (optionValues ?? []).filter(Boolean).map((value) => String(value).trim().toLowerCase());

    return triggers.some((trigger) => selected.includes(trigger));
  }

  /**
   * Shows or hides the block and keeps its input out of the product form while hidden.
   * @param {boolean} visible - Whether the block should be visible.
   */
  #setVisibility(visible) {
    const wrapper = this.refs.conditionalWrapper;
    if (!wrapper) return;

    // In the theme editor the block stays selectable, dimmed, instead of disappearing.
    const designMode = wrapper.dataset.designMode === 'true';
    wrapper.toggleAttribute('hidden', !visible && !designMode);
    wrapper.classList.toggle('is-conditionally-hidden', !visible && designMode);

    const isRequired = wrapper.dataset.required === 'true';

    const fields = /** @type {NodeListOf<HTMLInputElement | HTMLTextAreaElement>} */ (
      this.querySelectorAll('input, textarea')
    );

    for (const field of fields) {
      field.disabled = !visible;
      field.required = visible && isRequired;
      if (isRequired) field.setAttribute('aria-required', String(visible));
    }
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
